/**
 * Locale registry.
 *
 * §25 names fifteen launch languages and requires right-to-left layout,
 * locale-aware dates, numbers and sorting, and correct handling of scripts that
 * need shaping (Arabic) or composition (Indic). Those are properties of the
 * locale, not of any one screen, so they live here rather than being rediscovered
 * at each call site.
 */

export type LocaleCode =
  | 'ko' | 'en' | 'ja' | 'zh-Hans' | 'zh-Hant' | 'es' | 'fr' | 'de'
  | 'pt' | 'id' | 'vi' | 'th' | 'ar' | 'hi' | 'ru';

export type TextDirection = 'ltr' | 'rtl';

/**
 * Script behaviour that affects rendering rather than translation.
 * §25 calls these out individually because getting them wrong produces missing
 * glyphs or broken layout rather than merely awkward wording.
 */
export interface ScriptTraits {
  /** Arabic-style contextual joining; the shaper must not be bypassed. */
  readonly shaping: boolean;
  /** Indic-style consonant clusters; grapheme boundaries ≠ code points. */
  readonly composition: boolean;
  /** CJK ideographs — needs a CJK-capable font and different line breaking. */
  readonly cjk: boolean;
  /** No inter-word spaces (Thai, Japanese, Chinese) — line breaking is dictionary-based. */
  readonly spacelessWrapping: boolean;
}

export interface LocaleInfo {
  readonly code: LocaleCode;
  /** Endonym — shown in the language picker, always in its own script. */
  readonly nativeName: string;
  readonly englishName: string;
  readonly direction: TextDirection;
  readonly script: ScriptTraits;
  /**
   * Rough width factor versus English for the same message. Used by layout
   * tests to catch controls that only fit because the string was English
   * (§25 "Support variable text lengths").
   */
  readonly lengthFactor: number;
  /** BCP 47 tag passed to Intl. */
  readonly intlTag: string;
}

const plain: ScriptTraits = { shaping: false, composition: false, cjk: false, spacelessWrapping: false };
const cjk: ScriptTraits = { shaping: false, composition: false, cjk: true, spacelessWrapping: true };

export const LOCALES: readonly LocaleInfo[] = Object.freeze([
  { code: 'ko', nativeName: '한국어', englishName: 'Korean', direction: 'ltr', script: { ...plain, cjk: true }, lengthFactor: 0.9, intlTag: 'ko-KR' },
  { code: 'en', nativeName: 'English', englishName: 'English', direction: 'ltr', script: plain, lengthFactor: 1.0, intlTag: 'en-US' },
  { code: 'ja', nativeName: '日本語', englishName: 'Japanese', direction: 'ltr', script: cjk, lengthFactor: 0.85, intlTag: 'ja-JP' },
  { code: 'zh-Hans', nativeName: '简体中文', englishName: 'Chinese (Simplified)', direction: 'ltr', script: cjk, lengthFactor: 0.7, intlTag: 'zh-Hans-CN' },
  { code: 'zh-Hant', nativeName: '繁體中文', englishName: 'Chinese (Traditional)', direction: 'ltr', script: cjk, lengthFactor: 0.7, intlTag: 'zh-Hant-TW' },
  { code: 'es', nativeName: 'Español', englishName: 'Spanish', direction: 'ltr', script: plain, lengthFactor: 1.25, intlTag: 'es-ES' },
  { code: 'fr', nativeName: 'Français', englishName: 'French', direction: 'ltr', script: plain, lengthFactor: 1.25, intlTag: 'fr-FR' },
  { code: 'de', nativeName: 'Deutsch', englishName: 'German', direction: 'ltr', script: plain, lengthFactor: 1.35, intlTag: 'de-DE' },
  { code: 'pt', nativeName: 'Português', englishName: 'Portuguese', direction: 'ltr', script: plain, lengthFactor: 1.25, intlTag: 'pt-BR' },
  { code: 'id', nativeName: 'Bahasa Indonesia', englishName: 'Indonesian', direction: 'ltr', script: plain, lengthFactor: 1.2, intlTag: 'id-ID' },
  { code: 'vi', nativeName: 'Tiếng Việt', englishName: 'Vietnamese', direction: 'ltr', script: plain, lengthFactor: 1.15, intlTag: 'vi-VN' },
  { code: 'th', nativeName: 'ไทย', englishName: 'Thai', direction: 'ltr', script: { ...plain, composition: true, spacelessWrapping: true }, lengthFactor: 1.0, intlTag: 'th-TH' },
  { code: 'ar', nativeName: 'العربية', englishName: 'Arabic', direction: 'rtl', script: { ...plain, shaping: true }, lengthFactor: 1.0, intlTag: 'ar-SA' },
  { code: 'hi', nativeName: 'हिन्दी', englishName: 'Hindi', direction: 'ltr', script: { ...plain, composition: true }, lengthFactor: 1.1, intlTag: 'hi-IN' },
  { code: 'ru', nativeName: 'Русский', englishName: 'Russian', direction: 'ltr', script: plain, lengthFactor: 1.3, intlTag: 'ru-RU' },
]);

export const LOCALES_BY_CODE: ReadonlyMap<LocaleCode, LocaleInfo> =
  new Map(LOCALES.map((l) => [l.code, l]));

/** The locale every message is guaranteed to exist in. */
export const FALLBACK_LOCALE: LocaleCode = 'en';

export function isLocaleCode(value: string): value is LocaleCode {
  return LOCALES_BY_CODE.has(value as LocaleCode);
}

export function localeInfo(code: LocaleCode): LocaleInfo {
  const info = LOCALES_BY_CODE.get(code);
  // Unreachable for well-typed callers; guards against untyped platform input.
  if (!info) throw new Error(`unknown locale: ${code}`);
  return info;
}

/**
 * Pick the best supported locale for an OS language list (§25 "Automatically
 * detect the operating-system language").
 *
 * Matching is progressive: exact tag, then language+script, then bare language.
 * `zh` alone resolves to Simplified, which is what an unqualified Chinese
 * preference means in practice; `zh-TW`, `zh-HK` and `zh-MO` resolve to
 * Traditional because that is what those regions use.
 */
export function negotiateLocale(
  preferred: readonly string[],
  available: readonly LocaleCode[] = LOCALES.map((l) => l.code),
): LocaleCode {
  const supported = new Set(available);

  for (const raw of preferred) {
    const tag = raw.trim();
    if (!tag) continue;

    // Exact match against a supported code, case-insensitively.
    const exact = available.find((c) => c.toLowerCase() === tag.toLowerCase());
    if (exact) return exact;

    const parts = tag.split(/[-_]/);
    const lang = (parts[0] ?? '').toLowerCase();
    const rest = parts.slice(1).map((p) => p.toLowerCase());

    if (lang === 'zh') {
      const traditional = rest.some((p) => p === 'hant' || p === 'tw' || p === 'hk' || p === 'mo');
      const pick: LocaleCode = traditional ? 'zh-Hant' : 'zh-Hans';
      if (supported.has(pick)) return pick;
      continue;
    }

    const byLanguage = available.find((c) => c.split('-')[0]?.toLowerCase() === lang);
    if (byLanguage) return byLanguage;
  }

  return supported.has(FALLBACK_LOCALE) ? FALLBACK_LOCALE : (available[0] ?? FALLBACK_LOCALE);
}
